import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInvoiceStorageRecord } from 'services/billing/storageBillingInvoices.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { ICountryDetail } from '../../../types/countries';
import {
  BILLING_PROVIDER_TYPE,
  BordInvoiceInfo
} from 'components/BillingDataTab/Components/BordInvoiceInfo/BordInvoiceInfo';
import useResetData from 'hooks/useResetData';
import InvoiceRow from '../InvoiceRow/InvoiceRow';
import { IInvoiceStorageRecord, INVOICE_SECTION_VARIANT } from '../InvoiceSectionOfBillingTab.types';
import { NudosButton } from 'components/NudosComponents';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { fgDefault, nudosGray400 } from 'styles/styles.constants';
import './InvoiceSectionStorage.scss';

interface IInvoiceSectionStorageProps {
  storageInvoiceId: number;
  companyName: string;
  emissionCountry: string;
  emissionCountryFlag?: string;
  billingCountry?: ICountryDetail;
  hasIVA: boolean;
  billingProvider?: string;
  disabled?: boolean;
}

const InvoiceSectionStorage = ({
  storageInvoiceId,
  companyName,
  emissionCountry,
  emissionCountryFlag,
  billingCountry,
  hasIVA,
  billingProvider,
  disabled
}: IInvoiceSectionStorageProps) => {
  const [invoiceRecords, setInvoiceRecords] = useState<IInvoiceStorageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { resetStorageDataWithLoadersState } = useResetData();
  const isButtonAddInvoiceDisabled = invoiceRecords?.length >= 5 || invoiceRecords?.[0]?.id === undefined;

  const needsInvoiceUrl = !invoiceRecords?.[0]?.invoiceUrl;
  const needCreditInvoice = useMemo(() => {
    return needsInvoiceUrl && billingProvider === BILLING_PROVIDER_TYPE.BORD;
  }, [needsInvoiceUrl, billingProvider]);

  const getInvoiceRecords = useCallback(async () => {
    if (!storageInvoiceId) return;

    try {
      setIsLoading(true);
      const currInvoiceRecords = await getInvoiceStorageRecord(storageInvoiceId);
      if (!currInvoiceRecords || currInvoiceRecords?.length === 0) {
        setInvoiceRecords([{ type: INVOICE_SECTION_VARIANT.STORAGE, storageInvoiceId }]);
        return;
      }
      setInvoiceRecords(currInvoiceRecords);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  }, [storageInvoiceId]);

  const updateCurrentInvoiceRecord = useCallback((invoiceRecord: IInvoiceStorageRecord, index: number) => {
    setInvoiceRecords(prevRecords => [...prevRecords.slice(0, index), invoiceRecord, ...prevRecords.slice(index + 1)]);
  }, []);

  const createHandleUpdate = (index: number) => (updatedRecord: IInvoiceStorageRecord) => {
    updateCurrentInvoiceRecord(updatedRecord, index);
  };
  const handleAddInvoice = () => {
    setInvoiceRecords([...invoiceRecords, { type: INVOICE_SECTION_VARIANT.STORAGE, storageInvoiceId }]);
  };

  useEffect(() => {
    getInvoiceRecords();
  }, [getInvoiceRecords, resetStorageDataWithLoadersState]);

  return (
    <section className="invoiceSectionStorage">
      <div className="invoiceSectionStorageHeader">
        <p className="sectionTitle">Factura emitida por Bord</p>
      </div>
      <div className="internalDivider" />
      <div className="invoiceSectionStorageCard">
        <BordInvoiceInfo
          beneficiary={companyName || ''}
          countryName={emissionCountry || ''}
          countryEmissionFlag={emissionCountryFlag || ''}
          billingCountry={billingCountry}
          isInvoicingWithIVA={hasIVA}
          billingProvider={billingProvider}
        />
        {isLoading ? (
          <div className="invoiceRowContainerStorageSkeleton animationLoader" />
        ) : (
          invoiceRecords?.map((invoiceRecord, index) => (
            <div key={`invoice-storage-${index}`} className="invoiceRowContainerStorage">
              <InvoiceRow
                needCreditInvoice={needCreditInvoice}
                disabled={disabled}
                invoiceRecord={{ ...invoiceRecord, type: INVOICE_SECTION_VARIANT.STORAGE }}
                handleUpdateCurrentInvoiceRecord={createHandleUpdate(index)}
                customClassNameRow="customClassNameRow"
              />
            </div>
          ))
        )}
      </div>
      <NudosButton
        buttonText="Adjuntar otra factura"
        colorPalette="secondary"
        buttonIcon={
          <BordOneToneIcon
            variant="plus"
            customWidth="12px"
            stroke={isButtonAddInvoiceDisabled ? nudosGray400 : fgDefault}
          />
        }
        customClassName="addInvoiceButton"
        handleClick={handleAddInvoice}
        isButtonDisabled={isButtonAddInvoiceDisabled}
      />
    </section>
  );
};

export default InvoiceSectionStorage;
